import "../../CSS/home/home.css";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import CallIcon from "@mui/icons-material/Call";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StraightenIcon from "@mui/icons-material/Straighten";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HandymanOutlinedIcon from "@mui/icons-material/HandymanOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import SiteHeader from "../Layout/SiteHeader";
import { PageSeo } from "../Layout/Seo";
import SiteFooter from "../Layout/SiteFooter";
import ProductCard from "../Layout/ProductCard";
import CtaBand from "../Layout/CtaBand";
import { PHONE, PHONE_LINK } from "../Layout/contactInfo";
import products, { categories, productsIn } from "../ProductPage/products";
import HeroImage from "../../assets/home/hero-1920.webp";
import HeroImageSmall from "../../assets/home/hero-960.webp";

const yearsInBusiness = new Date().getFullYear() - 1999;

const stats = [
  { value: "1999", label: "Established in Bengaluru" },
  { value: "1st", label: "In India with magnetic mosquito nets" },
  { value: `${products.length}`, label: "Screen systems for doors & windows" },
  { value: "SS 304", label: "Rust-free stainless steel mesh" },
];

const advantages = [
  {
    icon: StraightenIcon,
    title: "Tailor-made fit",
    text: "Every screen is made to fit your windows and doors perfectly.",
  },
  {
    icon: HandymanOutlinedIcon,
    title: "No nailing",
    text: "We do not nail on your windows or doors.",
  },
  {
    icon: ShieldOutlinedIcon,
    title: "Built to last",
    text: "High durability and longevity, with quality testing at every stage.",
  },
  {
    icon: VisibilityOutlinedIcon,
    title: "Clear view",
    text: "High transparency mesh that keeps your view and airflow.",
  },
  {
    icon: WorkspacePremiumOutlinedIcon,
    title: "Enhances your interiors",
    text: "Well-made fixtures that complement your interior design.",
  },
  {
    icon: SupportAgentOutlinedIcon,
    title: "After-sales support",
    text: "Excellent and reliable support after installation.",
  },
];

function Home() {
  const [activeCategory, setActiveCategory] = useState("all");
  const shownProducts =
    activeCategory === "all" ? products : productsIn(activeCategory);

  return (
    <>
      <PageSeo path="/" />
      <SiteHeader />

      <main>
        {/* Hero */}
        <section className="dm-hero">
          <div className="dm-container dm-hero-grid">
            <div className="dm-hero-copy">
              <span className="dm-chip">Since 1999 · First in India with magnetic mosquito nets</span>
              <h1 className="dm-h1">
                Mosquito screens, <span className="dm-hero-accent">made to fit</span> your home.
              </h1>
              <p className="dm-lead">
                Magnetic, retractable, sliding and openable screens for your doors and
                windows. Custom made with rust-free mesh, and fitted without nailing into
                your frames.
              </p>
              <div className="dm-hero-actions">
                <Link to="/contactus" className="dm-btn dm-btn-primary">
                  Get a Quote <ArrowForwardIcon />
                </Link>
                <a href="#products" className="dm-btn dm-btn-outline">
                  Explore products
                </a>
              </div>
              <a href={PHONE_LINK} className="dm-hero-call">
                <span className="dm-hero-call-icon">
                  <CallIcon />
                </span>
                <span>
                  <small>Talk to us directly</small>
                  {PHONE}
                </span>
              </a>
            </div>

            <div className="dm-hero-media">
              <img
                src={HeroImage}
                srcSet={`${HeroImageSmall} 960w, ${HeroImage} 1920w`}
                sizes="(max-width: 960px) 100vw, 560px"
                alt="Mosquito screen being fitted"
                width="1920"
                height="1234"
                fetchpriority="high"
              />
              <div className="dm-hero-badge dm-hero-badge-top">
                <strong>{yearsInBusiness}+ years</strong>
                <span>of making screens</span>
              </div>
              <div className="dm-hero-badge dm-hero-badge-bottom">
                <strong>Custom made</strong>
                <span>for every door & window</span>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="dm-stats">
          <div className="dm-container dm-stats-grid">
            {stats.map((stat) => (
              <div className="dm-stat" key={stat.label}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Products */}
        <section className="dm-section" id="products">
          <div className="dm-container">
            <div className="dm-section-head">
              <p className="dm-eyebrow">Our products</p>
              <h2 className="dm-h2">A screen for every door and window</h2>
              <p className="dm-lead">
                Choose a system below to see how it works, the options it comes in and
                a video of it in use.
              </p>
            </div>

            <div className="dm-tabs" role="tablist" aria-label="Product categories">
              {[{ id: "all", name: "All" }, ...categories].map((category) => (
                <button
                  key={category.id}
                  type="button"
                  role="tab"
                  aria-selected={activeCategory === category.id}
                  className={`dm-tab ${activeCategory === category.id ? "active" : ""}`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>

            <div className="dm-product-grid">
              {shownProducts.map((product) => (
                <ProductCard product={product} key={product.path} />
              ))}
            </div>
          </div>
        </section>

        {/* Why Delmen */}
        <section className="dm-section dm-surface">
          <div className="dm-container">
            <div className="dm-section-head">
              <p className="dm-eyebrow">Why Delmen</p>
              <h2 className="dm-h2">Quality and consistency, since 1999</h2>
            </div>
            <div className="dm-features">
              {advantages.map(({ icon: Icon, title, text }) => (
                <div className="dm-feature" key={title}>
                  <span className="dm-feature-icon">
                    <Icon />
                  </span>
                  <h3 className="dm-h3">{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About */}
        <section className="dm-section">
          <div className="dm-container dm-about">
            <div className="dm-about-collage" aria-hidden="true">
              {products.slice(0, 4).map((product) => (
                <img src={product.image} alt="" loading="lazy" key={product.path} />
              ))}
            </div>
            <div>
              <p className="dm-eyebrow">About us</p>
              <h2 className="dm-h2">India's first magnetic mosquito nets</h2>
              <p className="dm-lead dm-about-text">
                Delmen Mosquito Screens™ was founded in 1999. We were the first in India
                to introduce magnetic mosquito nets, and have grown to become one of the
                most reputable manufacturers of magnetic insect screens, retractable
                roller systems, and sliding and openable systems for windows and doors.
              </p>
              <p className="dm-about-text">
                We offer customized end-to-end solutions and constantly develop our
                products in close consultation with the market, so they meet the
                specific requirements of our clients.
              </p>
              <Link to="/Readmore" className="dm-btn dm-btn-outline">
                Read more about us <ArrowForwardIcon />
              </Link>
            </div>
          </div>
        </section>

        <CtaBand />
      </main>

      <SiteFooter />
    </>
  );
}

export default Home;
